// from https://github.com/awslabs/aws-mobile-appsync-sdk-js#creating-a-client

import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, from } from "@apollo/client"
// import { SentryLink } from 'apollo-link-sentry';
import { AUTH_TYPE, createAuthLink } from "aws-appsync-auth-link"
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link"
import { appSyncUrl, region } from "../config/next"
import { getCognitoAccessJwtCached } from "./jwt"
import { Session } from "next-auth"
import { validateAuthToken } from "./utils"
import { handleSignOut } from "@web/hooks/useAuthState"
import { onError } from "@apollo/client/link/error"

export function getAppSyncConfig() {
  if (!region || !appSyncUrl) {
    console.debug("NEXT_PUBLIC_REGION and NEXT_PUBLIC_APPSYNC_ENDPOINT are not set; not authenticating with AppSync")
    return undefined
  }

  return { region, appSyncUrl }
}

const errorHandler = onError(({ graphQLErrors }) => {
  // Sign out if the user is unauthorized
  const isUnauthorized = graphQLErrors?.some(({ message }) => message === "Unauthorized")
  if (isUnauthorized) {
    handleSignOut().catch(console.error)
  }

  // return nothing to prevent retry
  return
})

async function getJwtToken(session?: Session | null) {
  const token = await getCognitoAccessJwtCached(session)
  if (!token) {
    // shouldn't ever get here!
    console.error("No access token found in session")
  }

  return token || ""
}

export const getApolloClient = (session?: Session | null, appSyncApiKey?: string | null) => {
  const appsyncConfig = getAppSyncConfig()

  let apolloLink
  if (appsyncConfig) {
    const { region, appSyncUrl } = appsyncConfig
    const auth = appSyncApiKey
      ? ({
          region,
          type: AUTH_TYPE.API_KEY,
          apiKey: appSyncApiKey,
        } as const)
      : ({
          region,
          type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
          jwtToken: () => getJwtToken(session),
        } as const)

    const httpLink = createHttpLink({ uri: appSyncUrl })

    apolloLink = ApolloLink.from([
      from([errorHandler]),
      ...(!appSyncApiKey
        ? [validateAuthToken({ getAuthToken: () => getJwtToken(session), logOut: handleSignOut })]
        : []),
      createAuthLink({ url: appSyncUrl, region, auth }) as unknown as ApolloLink,
      createSubscriptionHandshakeLink({ url: appSyncUrl, region, auth }, httpLink as any) as unknown as ApolloLink,
    ])
  }

  return new ApolloClient({
    link: apolloLink as ApolloLink,
    cache: new InMemoryCache({
      typePolicies: {
        // The response from the GetBillingStatus query doesn't have identifier, so it's better to explicitly set the merge function.
        // Will always accept the incoming data as the source of truth.
        GetBillingStatus: { merge: (existing: any, incoming: any) => incoming },
      },
    }),
    defaultOptions: {
      query: {
        fetchPolicy: "network-only",
      },
    },
  })
}
